import { useState } from 'react'
import type { components } from '@/api/generated/schema'
import { Breadcrumbs } from './Breadcrumbs'
import { AccountMenu } from '@/layers/shell/AccountMenu'
import { useDismissableMenu } from '@/hooks/useDismissableMenu'

type OrganisationMembership = components['schemas']['OrganisationMembership']
type WorkspaceSummary = components['schemas']['WorkspaceSummary']

/** `div.topbar` → `topbar` (process/08-COMPONENT-CATALOGUE.md). "The application header." §27.1:
 * "the indicator is always visible, not only on organisation-level screens" — the organisation
 * switcher renders here, on every screen this shell wraps.
 *
 * **`background` corrected from `bg-panel` (flat white) to `--gradient-topbar` (W173)** — the
 * prototype's own `.topbar` rule is `linear-gradient(100deg,#E3F6F7 0%,#F0F4FB 46%,#EFF0FA 72%,
 * #EAE7FA 100%)`, never a flat colour; this was the single most visible piece of the "why doesn't
 * this look like the prototype" gap, present on every screen since the shell was first built (W143).
 *
 * **`crumbs` (`Breadcrumbs.tsx`) added W180** — the prototype's own breadcrumb trail sits in every
 * `.topbar`, after both switchers; this element did not exist anywhere in this build before now.
 */
export function TopBar({
  organisations,
  currentOrgId,
  onSwitchOrg,
  workspaces,
  currentWorkspaceId,
  onSwitchWorkspace,
  onNavigate,
  onChangePassword,
  onSignOut,
}: {
  organisations: readonly OrganisationMembership[]
  currentOrgId: string
  onSwitchOrg: (orgId: string) => void
  workspaces: readonly WorkspaceSummary[]
  currentWorkspaceId: string | undefined
  onSwitchWorkspace: (workspaceId: string) => void
  onNavigate: (routeId: string) => void
  onChangePassword: () => void
  onSignOut: () => void
}) {
  const currentOrg = organisations.find((o) => o.org_id === currentOrgId)
  const currentWorkspace = workspaces.find((w) => w.workspace_id === currentWorkspaceId)

  return (
    <div data-cmp="topbar" className="fixed inset-x-0 top-0 h-10 flex items-center gap-5 px-6 border-b border-bd z-10" style={{ background: 'var(--gradient-topbar)' }}>
      <div data-cmp="brand" className="font-bold text-body-lg">
        Bitbucket Data Protection
      </div>

      <Switcher
        variant="organisation-switcher"
        currentLabel={currentOrg?.name ?? '—'}
        rows={organisations.map((org) => ({
          key: org.org_id,
          label: org.name,
          note: org.access_blocked ? 'Access blocked' : undefined,
          onSelect: () => onSwitchOrg(org.org_id),
        }))}
      />

      <Switcher
        variant="workspace-switcher"
        currentLabel={currentWorkspace?.name ?? '—'}
        rows={workspaces.map((ws) => ({ key: ws.workspace_id, label: ws.name, onSelect: () => onSwitchWorkspace(ws.workspace_id) }))}
      />

      <Breadcrumbs onNavigate={onNavigate} />

      <AccountMenu onChangePassword={onChangePassword} onSignOut={onSignOut} />
    </div>
  )
}

function Switcher({
  variant,
  currentLabel,
  rows,
}: {
  variant: 'organisation-switcher' | 'workspace-switcher'
  currentLabel: string
  rows: readonly { key: string; label: string; note?: string; onSelect: () => void }[]
}) {
  const [open, setOpen] = useState(false)
  const { containerRef, triggerRef } = useDismissableMenu(open, () => setOpen(false))
  return (
    <div data-cmp={variant} className="relative" ref={containerRef}>
      <button ref={triggerRef} className="flex items-center gap-3 h-8 px-4 rounded-2 border border-bd2 text-body bg-panel" onClick={() => setOpen((v) => !v)}>
        <b>{currentLabel}</b>
      </button>
      {open && (
        <div data-cmp="switcher-menu" className="absolute top-9 left-0 min-w-[260px] bg-panel border border-bd rounded-3 shadow-2 p-3 z-20">
          {rows.map((row) => (
            <button
              key={row.key}
              data-cmp="switcher-row"
              className="flex items-center justify-between w-full text-left px-3 py-3 rounded-2 hover:bg-row text-body"
              onClick={() => {
                row.onSelect()
                setOpen(false)
              }}
            >
              <span>{row.label}</span>
              {row.note && <span className="text-small text-fail">{row.note}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
